<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import ReportsAPI from 'dashboard/api/reports';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  LEADERBOARD_METRICS,
  TABLE_COLUMNS,
  currentMonthKey,
  hasAnyKpi,
  kpiLabelKey,
  metricValue,
  normalizeTeamPerformanceResponse,
  periodRange,
  productivityGroupsForRow,
  recentMonthOptions,
  sortLeaderboardRows,
} from '../utils/teamPerformance';

const router = useRouter();
const account = useAccount();
const { t } = useI18n();

const period = ref(currentMonthKey());
const mode = ref('table');
const leaderboardMetric = ref('finished_visits');
const sortBy = ref('progress');
const sortDir = ref('desc');
const page = ref(1);
const perPage = ref(25);
const rows = ref([]);
const meta = ref(normalizeTeamPerformanceResponse().meta);
const isLoading = ref(false);
const errorMessage = ref('');
const isForbidden = ref(false);
const selectedAgent = ref(null);

const kpiLabels = computed(() => ({
  client_with_activity: t('TEAM_PERFORMANCE.KPIS.CLIENT_WITH_ACTIVITY'),
  preferred_fixed_client: t('TEAM_PERFORMANCE.KPIS.PREFERRED_FIXED_CLIENT'),
  finished_visits: t('TEAM_PERFORMANCE.KPIS.FINISHED_VISITS'),
  total_booked_visits: t('TEAM_PERFORMANCE.KPIS.TOTAL_BOOKED_VISITS'),
  visits_booked_by_agent: t('TEAM_PERFORMANCE.KPIS.VISITS_BOOKED_BY_AGENT'),
  sent_offers: t('TEAM_PERFORMANCE.KPIS.SENT_OFFERS'),
  accepted_offers: t('TEAM_PERFORMANCE.KPIS.ACCEPTED_OFFERS'),
  signed_ccv: t('TEAM_PERFORMANCE.KPIS.SIGNED_CCV'),
}));
const columnLabels = computed(() => ({
  name: t('TEAM_PERFORMANCE.COLUMNS.AGENT'),
  client_with_activity: t('TEAM_PERFORMANCE.COLUMNS.CLIENTS'),
  finished_visits: t('TEAM_PERFORMANCE.COLUMNS.FINISHED_VISITS'),
  total_booked_visits: t('TEAM_PERFORMANCE.COLUMNS.BOOKED_VISITS'),
  sent_offers: t('TEAM_PERFORMANCE.COLUMNS.SENT_OFFERS'),
  accepted_offers: t('TEAM_PERFORMANCE.COLUMNS.ACCEPTED_OFFERS'),
  signed_ccv: t('TEAM_PERFORMANCE.COLUMNS.SIGNED_CCV'),
  target: t('TEAM_PERFORMANCE.COLUMNS.TARGET'),
  progress: t('TEAM_PERFORMANCE.COLUMNS.PROGRESS'),
  'delta.finished_visits': t('TEAM_PERFORMANCE.COLUMNS.TREND'),
}));
const groupLabels = computed(() => ({
  clients: t('TEAM_PERFORMANCE.GROUPS.CLIENTS'),
  visits: t('TEAM_PERFORMANCE.GROUPS.VISITS'),
  offers: t('TEAM_PERFORMANCE.GROUPS.OFFERS'),
}));

function kpiLabel(key) {
  return kpiLabels.value[key] || kpiLabelKey(key);
}

function columnLabel(column) {
  if (column.key === 'delta.finished_visits') {
    return t('TEAM_PERFORMANCE.COLUMNS.TREND_FOR', {
      metric: kpiLabel(leaderboardMetric.value),
    });
  }
  return columnLabels.value[column.key] || column.key;
}

function groupLabel(group) {
  return groupLabels.value[group.id] || group.labelKey;
}

const periodOptions = computed(() => recentMonthOptions());
const leaderboardMetricOptions = computed(() =>
  LEADERBOARD_METRICS.map(key => ({ value: key, label: kpiLabel(key) }))
);
const sortedLeaderboardRows = computed(() =>
  sortLeaderboardRows(rows.value, leaderboardMetric.value)
);
const hasRowsWithActivity = computed(() => hasAnyKpi(rows.value));
const isEmpty = computed(
  () => !isLoading.value && !errorMessage.value && !rows.value.length
);
const allRowsZero = computed(
  () =>
    !isLoading.value &&
    !errorMessage.value &&
    rows.value.length > 0 &&
    !hasRowsWithActivity.value
);
const totalPages = computed(() => Math.max(meta.value.total_pages || 1, 1));
// Drill-down reuses the already loaded row payload, so no drawer fetch/skeleton is required.
const selectedGroups = computed(() =>
  selectedAgent.value
    ? productivityGroupsForRow(selectedAgent.value, {
        groupLabel,
        kpiLabel,
      })
    : []
);
const visibleColumns = computed(() =>
  TABLE_COLUMNS.map(column => ({
    ...column,
    label: columnLabel(column),
  }))
);
const perPageOptions = computed(() =>
  [10, 25, 50, 100].map(value => ({
    value,
    label: t('TEAM_PERFORMANCE.PER_PAGE', { count: value }),
  }))
);
const rankingScopeLabel = computed(() =>
  t('TEAM_PERFORMANCE.RANKING.PAGE_SCOPE', {
    page: page.value,
    totalPages: totalPages.value,
  })
);

function formatPercent(value) {
  return `${Number(value || 0)}%`;
}

function formatTargetProgress(actual, target) {
  return `${Number(actual || 0)} / ${Number(target || 0)}`;
}

const summaryCards = computed(() => [
  {
    label: t('TEAM_PERFORMANCE.SUMMARY.AGENTS'),
    value: meta.value.total_count,
    helper: t('TEAM_PERFORMANCE.SUMMARY.AGENTS_HELPER'),
  },
  {
    label: t('TEAM_PERFORMANCE.SUMMARY.VISITS'),
    value: meta.value.totals.finished_visits || 0,
    helper: t('TEAM_PERFORMANCE.SUMMARY.VISITS_HELPER'),
  },
  {
    label: t('TEAM_PERFORMANCE.SUMMARY.AVG_PROGRESS'),
    value: formatPercent(meta.value.team_avg_progress),
    helper: t('TEAM_PERFORMANCE.SUMMARY.AVG_PROGRESS_HELPER'),
  },
  {
    label: t('TEAM_PERFORMANCE.SUMMARY.SIGNED_CCV'),
    value: meta.value.totals.signed_ccv || 0,
    helper: t('TEAM_PERFORMANCE.SUMMARY.SIGNED_CCV_HELPER'),
  },
]);

function progressWidth(value) {
  return `${Math.min(Math.max(Number(value || 0), 0), 100)}%`;
}

function leaderboardWidth(row) {
  const max = Math.max(
    ...sortedLeaderboardRows.value.map(item =>
      metricValue(item, leaderboardMetric.value)
    ),
    1
  );

  return `${Math.round((metricValue(row, leaderboardMetric.value) / max) * 100)}%`;
}

function deltaValue(row, metric = leaderboardMetric.value) {
  return metricValue(row, `delta.${metric}`);
}

function deltaClass(value) {
  if (value > 0) return 'text-emerald-700';
  if (value < 0) return 'text-rose-700';

  return 'text-lais-text-muted';
}

function formatDelta(value) {
  if (value > 0) return `+${value}`;

  return String(value);
}

function ariaSort(column) {
  if (sortBy.value !== column.key) return 'none';

  return sortDir.value === 'asc' ? 'ascending' : 'descending';
}

function updateSort(column) {
  if (!column.sortable) return;
  if (sortBy.value === column.key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = column.key;
    sortDir.value = column.key === 'name' ? 'asc' : 'desc';
  }
  page.value = 1;
}

function openDrilldown(row) {
  selectedAgent.value = row;
}

function closeDrilldown() {
  selectedAgent.value = null;
}

function openAgentActivity() {
  if (!selectedAgent.value) return;

  router.push(
    account.accountScopedRoute(
      'relatorio_index',
      {},
      {
        workspace: 'activity',
        user_id: selectedAgent.value.user.id,
        periodo: period.value,
      }
    )
  );
}

function previousPage() {
  page.value = Math.max(page.value - 1, 1);
}

function nextPage() {
  page.value = Math.min(page.value + 1, totalPages.value);
}

function requestParams() {
  const range = periodRange(period.value);
  return {
    since: range.since,
    until: range.until,
    sort_by: sortBy.value,
    sort_dir: sortDir.value,
    metric: leaderboardMetric.value,
    page: page.value,
    per_page: perPage.value,
  };
}

async function loadTeamPerformance() {
  isLoading.value = true;
  errorMessage.value = '';
  isForbidden.value = false;

  try {
    const response = await ReportsAPI.agentsProductivity(requestParams());
    const normalized = normalizeTeamPerformanceResponse(response);
    rows.value = normalized.rows;
    meta.value = normalized.meta;
  } catch (error) {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      isForbidden.value = true;
      errorMessage.value = '';
      return;
    }
    errorMessage.value =
      error?.response?.data?.errors?.[0] || t('TEAM_PERFORMANCE.ERROR.DEFAULT');
    useAlert(errorMessage.value);
  } finally {
    isLoading.value = false;
  }
}

function handleEscape(event) {
  if (event.key === 'Escape') closeDrilldown();
}

watch([period, sortBy, sortDir, page, perPage], loadTeamPerformance);

watch(period, () => {
  page.value = 1;
});

onMounted(() => {
  window.addEventListener('keydown', handleEscape);
  loadTeamPerformance();
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEscape);
});
</script>

<template>
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      :title="t('TEAM_PERFORMANCE.TITLE')"
      :helper="t('TEAM_PERFORMANCE.HELPER')"
    >
      <template #actions>
        <select
          v-model="period"
          class="h-10 min-w-[180px] rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text outline-none focus:border-lais-primary"
        >
          <option
            v-for="item in periodOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </select>
        <div
          class="inline-grid h-10 grid-cols-2 rounded-lais border border-lais-border bg-white p-1"
        >
          <button
            type="button"
            class="rounded-[5px] px-3 text-[12px] font-semibold transition"
            :class="
              mode === 'table'
                ? 'bg-lais-primary text-white'
                : 'text-lais-text-muted hover:text-lais-text'
            "
            @click="mode = 'table'"
          >
            {{ t('TEAM_PERFORMANCE.MODE.TABLE') }}
          </button>
          <button
            type="button"
            class="rounded-[5px] px-3 text-[12px] font-semibold transition"
            :class="
              mode === 'ranking'
                ? 'bg-lais-primary text-white'
                : 'text-lais-text-muted hover:text-lais-text'
            "
            @click="mode = 'ranking'"
          >
            {{ t('TEAM_PERFORMANCE.MODE.RANKING') }}
          </button>
        </div>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div
        v-if="isForbidden"
        class="flex min-h-[420px] items-center justify-center"
      >
        <LaisCard class="max-w-[460px] text-center">
          <span
            class="i-lucide-lock mx-auto mb-3 block size-9 text-lais-text-muted"
            aria-hidden="true"
          />
          <h2 class="text-[18px] font-semibold text-lais-text">
            {{ t('TEAM_PERFORMANCE.FORBIDDEN.TITLE') }}
          </h2>
          <p class="mt-2 text-[13px] leading-5 text-lais-text-muted">
            {{ t('TEAM_PERFORMANCE.FORBIDDEN.BODY') }}
          </p>
        </LaisCard>
      </div>

      <template v-else>
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <LaisCard
            v-for="item in summaryCards"
            :key="item.label"
            density="tight"
          >
            <p class="text-[12px] font-semibold text-lais-text-muted">
              {{ item.label }}
            </p>
            <p class="mt-2 text-[28px] font-semibold text-lais-text">
              {{ item.value }}
            </p>
            <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
              {{ item.helper }}
            </p>
          </LaisCard>
        </div>

        <div v-if="errorMessage" class="mt-5">
          <LaisCard density="tight">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-[14px] font-semibold text-rose-800">
                  {{ t('TEAM_PERFORMANCE.ERROR.TITLE') }}
                </p>
                <p class="mt-1 text-[12px] text-lais-text-muted">
                  {{ errorMessage }}
                </p>
              </div>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-refresh-ccw"
                @click="loadTeamPerformance"
              >
                {{ t('TEAM_PERFORMANCE.ERROR.RETRY') }}
              </LaisPrimaryButton>
            </div>
          </LaisCard>
        </div>

        <LaisCard v-else class="mt-5" density="flush">
          <template #header>
            <div class="flex min-w-0 flex-col gap-1">
              <h2 class="text-[16px] font-semibold text-lais-text">
                {{ t('TEAM_PERFORMANCE.CARD.TITLE') }}
              </h2>
              <p class="text-[12px] text-lais-text-muted">
                {{ t('TEAM_PERFORMANCE.CARD.SUBTITLE') }}
              </p>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <select
                v-model="leaderboardMetric"
                :aria-label="t('TEAM_PERFORMANCE.RANKING.METRIC_LABEL')"
                class="h-9 rounded-lais border border-lais-border bg-white px-3 text-[12px] font-semibold text-lais-text outline-none focus:border-lais-primary"
              >
                <option
                  v-for="item in leaderboardMetricOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </option>
              </select>
              <select
                v-model.number="perPage"
                class="h-9 rounded-lais border border-lais-border bg-white px-3 text-[12px] font-semibold text-lais-text outline-none focus:border-lais-primary"
              >
                <option
                  v-for="item in perPageOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </option>
              </select>
              <LaisPrimaryButton
                variant="ghost"
                size="sm"
                icon="i-lucide-refresh-ccw"
                :is-loading="isLoading"
                @click="loadTeamPerformance"
              >
                {{ t('TEAM_PERFORMANCE.REFRESH') }}
              </LaisPrimaryButton>
            </div>
          </template>

          <div
            v-if="isLoading"
            class="grid gap-3 p-5"
            :aria-label="t('TEAM_PERFORMANCE.LOADING')"
          >
            <div
              v-for="item in 6"
              :key="item"
              class="h-14 rounded-lais bg-lais-bg-subtle"
            />
          </div>

          <div
            v-else-if="isEmpty || allRowsZero"
            class="flex min-h-[260px] flex-col items-center justify-center px-6 text-center"
          >
            <span
              class="i-lucide-chart-no-axes-combined mb-3 size-9 text-lais-text-muted"
              aria-hidden="true"
            />
            <h3 class="text-[15px] font-semibold text-lais-text">
              {{ t('TEAM_PERFORMANCE.EMPTY.TITLE') }}
            </h3>
            <p
              class="mt-2 max-w-[420px] text-[13px] leading-5 text-lais-text-muted"
            >
              {{ t('TEAM_PERFORMANCE.EMPTY.BODY') }}
            </p>
          </div>

          <template v-else-if="mode === 'ranking'">
            <div class="grid gap-3 p-5">
              <p
                class="rounded-lais bg-lais-bg-subtle px-3 py-2 text-[12px] font-semibold text-lais-text-muted"
              >
                {{ rankingScopeLabel }}
              </p>
              <button
                v-for="(row, index) in sortedLeaderboardRows"
                :key="row.user.id"
                type="button"
                class="grid gap-3 rounded-lais border border-lais-border-soft bg-white p-4 text-left transition hover:border-lais-primary"
                @click="openDrilldown(row)"
              >
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <div class="flex min-w-0 items-center gap-3">
                    <span
                      class="flex size-8 shrink-0 items-center justify-center rounded-full bg-lais-primary-50 text-[12px] font-bold text-lais-primary"
                    >
                      {{ index + 1 }}
                    </span>
                    <div class="min-w-0">
                      <p
                        class="truncate text-[14px] font-semibold text-lais-text"
                      >
                        {{ row.user.name }}
                      </p>
                      <p class="truncate text-[12px] text-lais-text-muted">
                        {{ row.user.email }}
                      </p>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-[18px] font-semibold text-lais-text">
                      {{ metricValue(row, leaderboardMetric) }}
                    </p>
                    <p
                      class="text-[12px] font-semibold"
                      :class="deltaClass(deltaValue(row))"
                    >
                      {{
                        t('TEAM_PERFORMANCE.RANKING.VS_PREVIOUS', {
                          delta: formatDelta(deltaValue(row)),
                        })
                      }}
                    </p>
                  </div>
                </div>
                <div class="h-2 rounded-full bg-lais-bg-subtle">
                  <div
                    class="h-2 rounded-full bg-lais-primary"
                    :style="{ width: leaderboardWidth(row) }"
                  />
                </div>
              </button>
            </div>
          </template>

          <template v-else>
            <div class="hidden overflow-x-auto md:block">
              <table class="min-w-[1080px] w-full table-fixed text-left">
                <thead class="bg-lais-bg-subtle">
                  <tr>
                    <th
                      v-for="column in visibleColumns"
                      :key="column.key"
                      scope="col"
                      class="px-4 py-3 text-[11px] font-bold uppercase text-lais-text-muted"
                      :aria-sort="ariaSort(column)"
                    >
                      <button
                        v-if="column.sortable"
                        type="button"
                        class="inline-flex items-center gap-1"
                        @click="updateSort(column)"
                      >
                        {{ column.label }}
                        <span
                          class="i-lucide-arrow-up-down size-3.5"
                          aria-hidden="true"
                        />
                      </button>
                      <span v-else>{{ column.label }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-lais-border-soft">
                  <tr
                    v-for="row in rows"
                    :key="row.user.id"
                    class="cursor-pointer transition hover:bg-lais-primary-50/30"
                    @click="openDrilldown(row)"
                  >
                    <td class="px-4 py-4">
                      <p
                        class="truncate text-[13px] font-semibold text-lais-text"
                      >
                        {{ row.user.name }}
                      </p>
                      <p class="truncate text-[11px] text-lais-text-muted">
                        {{ row.user.email }}
                      </p>
                    </td>
                    <td class="px-4 py-4 text-[13px] text-lais-text">
                      {{ row.kpis.client_with_activity }}
                    </td>
                    <td class="px-4 py-4 text-[13px] text-lais-text">
                      {{ row.kpis.finished_visits }}
                    </td>
                    <td class="px-4 py-4 text-[13px] text-lais-text">
                      {{ row.kpis.total_booked_visits }}
                    </td>
                    <td class="px-4 py-4 text-[13px] text-lais-text">
                      {{ row.kpis.sent_offers }}
                    </td>
                    <td class="px-4 py-4 text-[13px] text-lais-text">
                      {{ row.kpis.accepted_offers }}
                    </td>
                    <td class="px-4 py-4 text-[13px] text-lais-text">
                      {{ row.kpis.signed_ccv }}
                    </td>
                    <td class="px-4 py-4 text-[13px] text-lais-text">
                      {{ row.target.target_value }}
                    </td>
                    <td class="px-4 py-4">
                      <div class="flex min-w-[96px] items-center gap-2">
                        <div class="h-2 flex-1 rounded-full bg-lais-bg-subtle">
                          <div
                            class="h-2 rounded-full bg-lais-primary"
                            :style="{ width: progressWidth(row.progress) }"
                          />
                        </div>
                        <span class="w-9 text-right text-[12px] font-semibold">
                          {{ formatPercent(row.progress) }}
                        </span>
                      </div>
                    </td>
                    <td
                      class="px-4 py-4 text-[13px] font-semibold"
                      :class="deltaClass(deltaValue(row))"
                    >
                      {{ formatDelta(deltaValue(row)) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot class="border-t border-lais-border bg-white">
                  <tr>
                    <td class="px-4 py-3 text-[12px] font-bold text-lais-text">
                      {{ t('TEAM_PERFORMANCE.FOOTER.TOTALS') }}
                    </td>
                    <td class="px-4 py-3 text-[12px] font-bold">
                      {{ meta.totals.client_with_activity || 0 }}
                    </td>
                    <td class="px-4 py-3 text-[12px] font-bold">
                      {{ meta.totals.finished_visits || 0 }}
                    </td>
                    <td class="px-4 py-3 text-[12px] font-bold">
                      {{ meta.totals.total_booked_visits || 0 }}
                    </td>
                    <td class="px-4 py-3 text-[12px] font-bold">
                      {{ meta.totals.sent_offers || 0 }}
                    </td>
                    <td class="px-4 py-3 text-[12px] font-bold">
                      {{ meta.totals.accepted_offers || 0 }}
                    </td>
                    <td class="px-4 py-3 text-[12px] font-bold">
                      {{ meta.totals.signed_ccv || 0 }}
                    </td>
                    <td class="px-4 py-3 text-[12px] font-bold">
                      {{ t('TEAM_PERFORMANCE.FOOTER.NO_VALUE') }}
                    </td>
                    <td class="px-4 py-3 text-[12px] font-bold">
                      {{ formatPercent(meta.team_avg_progress) }}
                    </td>
                    <td class="px-4 py-3 text-[12px] font-bold">
                      {{ t('TEAM_PERFORMANCE.FOOTER.NO_VALUE') }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div class="grid gap-3 p-4 md:hidden">
              <button
                v-for="row in rows"
                :key="row.user.id"
                type="button"
                class="grid gap-3 rounded-lais border border-lais-border-soft bg-white p-4 text-left"
                @click="openDrilldown(row)"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p
                      class="truncate text-[14px] font-semibold text-lais-text"
                    >
                      {{ row.user.name }}
                    </p>
                    <p class="truncate text-[12px] text-lais-text-muted">
                      {{ row.user.email }}
                    </p>
                  </div>
                  <p
                    class="shrink-0 text-[12px] font-semibold"
                    :class="deltaClass(deltaValue(row))"
                  >
                    {{ formatDelta(deltaValue(row)) }}
                  </p>
                </div>
                <div class="grid grid-cols-2 gap-2 text-[12px]">
                  <span>
                    {{
                      t('TEAM_PERFORMANCE.MOBILE.CLIENTS', {
                        value: row.kpis.client_with_activity,
                      })
                    }}
                  </span>
                  <span>
                    {{
                      t('TEAM_PERFORMANCE.MOBILE.VISITS', {
                        value: row.kpis.finished_visits,
                      })
                    }}
                  </span>
                  <span>
                    {{
                      t('TEAM_PERFORMANCE.MOBILE.OFFERS', {
                        value: row.kpis.sent_offers,
                      })
                    }}
                  </span>
                  <span>
                    {{
                      t('TEAM_PERFORMANCE.MOBILE.CCV', {
                        value: row.kpis.signed_ccv,
                      })
                    }}
                  </span>
                </div>
                <div class="h-2 rounded-full bg-lais-bg-subtle">
                  <div
                    class="h-2 rounded-full bg-lais-primary"
                    :style="{ width: progressWidth(row.progress) }"
                  />
                </div>
              </button>
            </div>
          </template>

          <template #footer>
            <div class="flex flex-wrap items-center justify-between gap-3">
              <p class="text-[12px] text-lais-text-muted">
                {{
                  t('TEAM_PERFORMANCE.FOOTER.PAGINATION', {
                    page,
                    totalPages,
                    total: meta.total_count,
                  })
                }}
              </p>
              <div class="flex items-center gap-2">
                <LaisPrimaryButton
                  variant="ghost"
                  size="sm"
                  :disabled="page <= 1"
                  icon="i-lucide-chevron-left"
                  @click="previousPage"
                >
                  {{ t('TEAM_PERFORMANCE.FOOTER.PREV') }}
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  variant="ghost"
                  size="sm"
                  :disabled="page >= totalPages"
                  icon="i-lucide-chevron-right"
                  @click="nextPage"
                >
                  {{ t('TEAM_PERFORMANCE.FOOTER.NEXT') }}
                </LaisPrimaryButton>
              </div>
            </div>
          </template>
        </LaisCard>
      </template>
    </div>

    <div
      v-if="selectedAgent"
      class="fixed inset-0 z-50 flex justify-end bg-black/30"
      role="presentation"
      @click.self="closeDrilldown"
    >
      <aside
        class="h-full w-full max-w-[520px] overflow-y-auto bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        :aria-label="t('TEAM_PERFORMANCE.DRAWER.ARIA')"
        tabindex="-1"
      >
        <header
          class="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-lais-border bg-white px-5 py-4"
        >
          <div class="min-w-0">
            <p class="text-[12px] font-semibold uppercase text-lais-text-muted">
              {{ t('TEAM_PERFORMANCE.DRAWER.EYEBROW') }}
            </p>
            <h2 class="truncate text-[20px] font-semibold text-lais-text">
              {{ selectedAgent.user.name }}
            </h2>
            <p class="truncate text-[12px] text-lais-text-muted">
              {{ selectedAgent.user.email }}
            </p>
          </div>
          <button
            type="button"
            class="inline-flex size-9 items-center justify-center rounded-full border border-lais-border text-lais-text-muted hover:text-lais-text"
            :aria-label="t('TEAM_PERFORMANCE.DRAWER.CLOSE')"
            @click="closeDrilldown"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </header>

        <div class="grid gap-5 p-5">
          <div
            class="rounded-lais border border-lais-border-soft bg-lais-bg-subtle p-4"
          >
            <div class="flex items-center justify-between gap-3">
              <div>
                <p class="text-[12px] font-semibold text-lais-text-muted">
                  {{ t('TEAM_PERFORMANCE.DRAWER.VISIT_TARGET') }}
                </p>
                <p class="mt-1 text-[22px] font-semibold text-lais-text">
                  {{
                    formatTargetProgress(
                      selectedAgent.target.actual,
                      selectedAgent.target.target_value
                    )
                  }}
                </p>
              </div>
              <span class="text-[18px] font-semibold text-lais-primary">
                {{ formatPercent(selectedAgent.progress) }}
              </span>
            </div>
            <div class="mt-3 h-2 rounded-full bg-white">
              <div
                class="h-2 rounded-full bg-lais-primary"
                :style="{ width: progressWidth(selectedAgent.progress) }"
              />
            </div>
          </div>

          <section
            v-for="group in selectedGroups"
            :key="group.label"
            class="rounded-lais border border-lais-border-soft bg-white p-4"
          >
            <div class="mb-3 flex items-center gap-2">
              <span
                class="size-4 text-lais-primary"
                :class="group.icon"
                aria-hidden="true"
              />
              <h3 class="text-[14px] font-semibold text-lais-text">
                {{ group.label }}
              </h3>
            </div>
            <div class="grid gap-2">
              <div
                v-for="metric in group.metrics"
                :key="metric.key"
                class="flex items-center justify-between gap-3 rounded-lais bg-lais-bg-subtle px-3 py-2"
              >
                <span class="text-[12px] text-lais-text-muted">
                  {{ metric.label }}
                </span>
                <span class="text-[13px] font-semibold text-lais-text">
                  {{ metric.value }}
                  <span
                    class="ml-2 text-[11px]"
                    :class="deltaClass(metric.delta)"
                  >
                    {{ formatDelta(metric.delta) }}
                  </span>
                </span>
              </div>
            </div>
          </section>

          <LaisPrimaryButton
            icon="i-lucide-arrow-up-right"
            @click="openAgentActivity"
          >
            {{ t('TEAM_PERFORMANCE.DRAWER.OPEN_ACTIVITY') }}
          </LaisPrimaryButton>
        </div>
      </aside>
    </div>
  </section>
</template>
